import { useEffect, useRef, useState } from 'react';

import {
  UseFormClearErrors,
  UseFormResetField,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';

import { RateSource } from '@/modules/common/types/rate-source';
import { RateMask } from '@/modules/common/utils/rate-mask';
import { AssignmentFormValues } from '@/modules/planner/components/modals/assignment-modal/assignment-modal';
import {
  FIELD_CURRENCY,
  FIELD_IS_BILLABLE,
  FIELD_RATE,
  FIELD_RATE_SOURCE,
} from '@/modules/planner/constants/assignment-modal-constants';
import { TeamMemberRatesOnProjectResponse } from '@/modules/team';
import { logger } from '@/utils/logger/logger';

import { getRateOptionsFromApi } from '../../helpers';
import { RateOption } from '../../types';

const TAG = 'useAssignmentRateManagement:';

interface UseAssignmentRateManagementParams {
  watch: UseFormWatch<AssignmentFormValues>;
  setValue: UseFormSetValue<AssignmentFormValues>;
  resetField: UseFormResetField<AssignmentFormValues>;
  clearErrors: UseFormClearErrors<AssignmentFormValues>;
  trigger: UseFormTrigger<AssignmentFormValues>;
  ratesData: TeamMemberRatesOnProjectResponse | undefined;
  rateOptions: RateOption[];
  isBeforeContractStart: boolean;
  initialAssignmentId?: number;
  isBillable: boolean;
  rateSource: RateSource | undefined;
  currentRate: string;
  currentCurrency: string;
}

/**
 * Manages all reactive rate-field logic for the assignment modal:
 * - Clears a masked rate when the user switches to Custom
 * - Updates rate/currency when the rate source changes
 * - Sets defaults from API data in create mode
 * - Resets ProjectBased selection when the date is before the contract start
 * - Handles the billable toggle (saving and restoring rate values)
 */
export function useAssignmentRateManagement({
  watch,
  setValue,
  resetField,
  clearErrors,
  trigger,
  ratesData,
  rateOptions,
  isBeforeContractStart,
  initialAssignmentId,
  isBillable,
  rateSource,
  currentRate,
  currentCurrency,
}: UseAssignmentRateManagementParams) {
  // Store rate values before isBillable was unchecked, so we can restore them
  const [savedRateValues, setSavedRateValues] = useState<{
    rateSource?: RateSource;
    rate: string;
    currency: string;
  } | null>(null);

  // In edit mode the form already has the correct rate/currency from initialData.
  // We must skip the rate-sync effect on the very first ratesData load to avoid
  // overwriting those values with API-derived ones (which may differ in format/value)
  // and falsely dirtying the form. The effect should only run on subsequent fires
  // that are triggered by the user actively changing rateSource.
  const hasRatesDataLoadedOnceRef = useRef(false);

  // Tracks the PREVIOUS rate source so the cleanup below can tell a user-initiated switch to
  // Custom from Custom simply being the initial value. Undefined on mount (and after a reopen,
  // see resetSavedRateValues) — without it, opening an existing Custom assignment whose rate the
  // backend masked would wipe the mask before the user touched anything, and the form would then
  // demand a rate they are not allowed to read.
  const prevRateSourceRef = useRef<RateSource | undefined>(undefined);

  // The masked rate taken out of the field when the user switched to Custom, kept so switching
  // back to a derived source can put it back. The sync effect below normally refills the rate for
  // derived sources, but it needs `ratesData`, and in edit mode the rates query is skipped while
  // the project and dates are untouched — so without this the field would just stay empty.
  const maskedRateBeforeCustomRef = useRef<string | null>(null);

  // Keep the rate in step with user-initiated rate-source switches while the value is masked.
  // Comparing against the previous source (rather than just reading the current one) is what
  // tells a real switch from Custom merely being the initial value on mount.
  useEffect(() => {
    const prevRateSource = prevRateSourceRef.current;
    prevRateSourceRef.current = rateSource;

    if (prevRateSource === undefined || prevRateSource === rateSource) return;

    // Switching TO Custom: the user is choosing to set their own rate, and a value they are not
    // allowed to see must not be carried over as if they had entered it.
    if (rateSource === RateSource.Custom) {
      if (!RateMask.isMaskedStringValue(currentRate)) return;

      maskedRateBeforeCustomRef.current = currentRate;
      setValue(FIELD_RATE, '');

      // Validate so formState.isValid — and with it the submit button — reflects the now-empty
      // required field, then drop the message: the user has not had a chance to fill it in yet.
      // clearErrors does not re-run validation, so isValid stays false and the message comes
      // back on their first edit or submit attempt.
      //
      // This must wait for the resolver to settle. zodResolver is async, so clearing
      // synchronously (or via setValue's shouldValidate) is undone by the validation result
      // landing a tick later, and the user sees "Rate is required" the moment they pick Custom.
      void trigger(FIELD_RATE).then(() => clearErrors(FIELD_RATE));
      return;
    }

    // Switching AWAY from Custom, back to a source whose rate the API owns. Restore the mask we
    // took so the field shows the masked rate again instead of an empty box. Every source reads
    // as masked for this user, so the stashed sentinel is the right value for any of them — and
    // when `ratesData` is available the sync effect below writes the same sentinel anyway.
    if (prevRateSource === RateSource.Custom && maskedRateBeforeCustomRef.current !== null) {
      setValue(FIELD_RATE, maskedRateBeforeCustomRef.current, { shouldDirty: false });
      maskedRateBeforeCustomRef.current = null;
    }
  }, [rateSource, currentRate, setValue, trigger, clearErrors]);

  // Update rate and currency when rate source changes.
  // Skips the initial ratesData load in edit mode — rate/currency are already
  // correct from initialData and must not be overwritten by the API response.
  useEffect(() => {
    if (!rateSource || !ratesData) return;

    const rateOpts = getRateOptionsFromApi({
      ratesData,
      assignmentOverride: currentRate ? parseFloat(currentRate) : undefined,
    });
    const opt = rateOpts.find(o => o.value === rateSource);

    const nextRate =
      rateSource !== RateSource.Custom && typeof opt?.rate === 'number'
        ? String(opt.rate)
        : rateSource === RateSource.Custom && !currentRate
          ? ''
          : null; // null means: no change needed

    const nextCurrency =
      rateSource !== RateSource.Custom && opt?.currency
        ? opt.currency
        : rateSource === RateSource.Custom && !watch(FIELD_CURRENCY)
          ? ratesData.currency.code
          : null; // null means: no change needed

    if (initialAssignmentId && !hasRatesDataLoadedOnceRef.current) {
      hasRatesDataLoadedOnceRef.current = true;
      // For Custom source the rate is a real stored value — don't overwrite it.
      // For all other sources (ProjectBased, GlobalDefaultRate, etc.) the rate is
      // always derived from the API (the assignment stores no rate of its own).
      // We must both set the value AND reset the default so compareFormValues
      // doesn't treat the API-derived rate as a user change.
      if (rateSource === RateSource.Custom) return;
      // Use resetField() (not setValue) so that both the form value AND the
      // default are updated — this prevents compareFormValues from treating
      // API-derived values as user changes. Currency needs the same treatment
      // as rate: the backend may not store a currency on non-Custom assignments
      // (rateCurrency is null), so the form starts with empty currency.
      //
      // resetField, NOT a whole-form reset(values => ...): a full reset bakes
      // every CURRENT value into the defaults, so anything the user changed
      // before this first (possibly slow) rates response landed — e.g. an
      // already-switched project — became a "default" and permanently froze
      // the dirty-comparator, leaving the submit button disabled.
      if (nextRate !== null) {
        resetField(FIELD_RATE, { defaultValue: nextRate });
      }
      if (!currentCurrency && nextCurrency !== null) {
        resetField(FIELD_CURRENCY, { defaultValue: nextCurrency });
      }
      return;
    }
    hasRatesDataLoadedOnceRef.current = true;

    if (nextRate !== null) setValue(FIELD_RATE, nextRate, { shouldDirty: false });
    if (nextCurrency !== null) setValue(FIELD_CURRENCY, nextCurrency, { shouldDirty: false });
  }, [rateSource, ratesData, currentRate, setValue, resetField, initialAssignmentId]);

  // Set rate source and rate from projectBasedRateOption when rates data is fetched.
  // Only for CREATE mode — set default to ProjectBased.
  useEffect(() => {
    if (ratesData && !initialAssignmentId) {
      const apiCurrency = ratesData.currency.code;

      // Non-billable from API takes priority: lock to Custom / 0
      // Defer setValue calls to the next microtask to avoid stale closures
      // when react-hook-form's reset() and setValue() run in the same render cycle
      if (!ratesData.isBillable) {
        setTimeout(() => {
          setValue(FIELD_IS_BILLABLE, false);
          setValue(FIELD_RATE_SOURCE, RateSource.Custom, { shouldValidate: true });
          setValue(FIELD_RATE, '0', { shouldValidate: true });
          setValue(FIELD_CURRENCY, apiCurrency, { shouldValidate: true });
        }, 0);
        return;
      }

      if (ratesData.projectBasedRateOption) {
        const projectRate = ratesData.projectBasedRateOption.rate;

        // Validate that we have a valid rate
        if (typeof projectRate !== 'number') {
          logger.warn(TAG, 'Invalid project rate', { projectRate });
          return;
        }

        // If start date is before contract, ProjectBased is unavailable
        // Deferred to avoid stale closures when multiple setValue calls follow reset()
        if (isBeforeContractStart) {
          setTimeout(() => {
            setValue(FIELD_RATE_SOURCE, undefined, { shouldValidate: true });
            setValue(FIELD_RATE, '', { shouldValidate: true });
            setValue(FIELD_CURRENCY, apiCurrency, { shouldValidate: true });
          }, 0);
          return;
        }

        // Only set default rate source for NEW assignments (create mode)
        // Deferred to avoid stale closures when multiple setValue calls follow reset()
        setTimeout(() => {
          setValue(FIELD_RATE_SOURCE, RateSource.ProjectBased, { shouldValidate: true });
          setValue(FIELD_RATE, String(projectRate), { shouldValidate: true });
          setValue(FIELD_CURRENCY, apiCurrency, { shouldValidate: true });
        }, 0);
      }
    }
  }, [ratesData, initialAssignmentId, isBeforeContractStart, setValue]);

  // When the assignment start date moves before the contract effective date,
  // ProjectBased is removed from the options — reset the selection to Custom
  // so the form doesn't hold an invisible/invalid value.
  useEffect(() => {
    if (isBeforeContractStart && rateSource === RateSource.ProjectBased) {
      if (!isBillable) {
        // Non-billable takes priority: lock to Custom / 0 so the form stays valid
        setValue(FIELD_RATE_SOURCE, RateSource.Custom, { shouldValidate: true });
        setValue(FIELD_RATE, '0', { shouldValidate: true });
      } else {
        // Clear to undefined so the existing schema refine fires and marks
        // rateSource as required — both fields show an error, not just rate.
        setValue(FIELD_RATE_SOURCE, undefined, { shouldValidate: true });
        setValue(FIELD_RATE, '', { shouldValidate: true });
      }
    }
  }, [isBeforeContractStart, rateSource, isBillable, setValue]);

  function handleBillableChange(checked: boolean) {
    const currency = watch(FIELD_CURRENCY);
    if (!checked) {
      // Save current rate values before overriding
      setSavedRateValues({ rateSource, rate: currentRate, currency });
      setValue(FIELD_IS_BILLABLE, false);
      setValue(FIELD_RATE_SOURCE, RateSource.Custom, { shouldValidate: true });
      setValue(FIELD_RATE, '0', { shouldValidate: true });
      // Ensure currency is set — in edit mode with non-Custom rate sources,
      // the form currency may still be empty because the initial rates load
      // skips writing it. Fall back to the API currency so validation passes.
      if (!currency && ratesData?.currency.code) {
        setValue(FIELD_CURRENCY, ratesData.currency.code, { shouldValidate: true });
      }
    } else {
      setValue(FIELD_IS_BILLABLE, true);

      if (savedRateValues) {
        // Restore previously saved values, but if the saved source was ProjectBased
        // and it's now unavailable (start before contract date), fall back to GlobalDefaultRate.
        const restoredSource =
          isBeforeContractStart && savedRateValues.rateSource === RateSource.ProjectBased
            ? RateSource.GlobalDefaultRate
            : savedRateValues.rateSource;
        const restoredRate =
          restoredSource !== savedRateValues.rateSource
            ? String(rateOptions.find(o => o.value === RateSource.GlobalDefaultRate)?.rate ?? '')
            : savedRateValues.rate;
        setValue(FIELD_RATE_SOURCE, restoredSource, { shouldValidate: true });
        setValue(FIELD_RATE, restoredRate, { shouldValidate: true });
        // Restore saved currency, falling back to API currency if it was empty
        setValue(FIELD_CURRENCY, savedRateValues.currency || ratesData?.currency.code || '', {
          shouldValidate: true,
        });
        setSavedRateValues(null);
      } else if (ratesData?.projectBasedRateOption) {
        // No saved values (was unchecked from the start) — default to ProjectBased from API,
        // or GlobalDefaultRate if the start date is before the contract effective date.
        const apiCurrency = ratesData.currency.code;
        if (isBeforeContractStart) {
          const userExternalOption = rateOptions.find(o => o.value === RateSource.UserExternalRate);
          setValue(FIELD_RATE_SOURCE, RateSource.GlobalDefaultRate, { shouldValidate: true });
          setValue(
            FIELD_RATE,
            typeof userExternalOption?.rate === 'number' ? String(userExternalOption.rate) : '',
            { shouldValidate: true }
          );
        } else {
          const projectRate = ratesData.projectBasedRateOption.rate;
          setValue(FIELD_RATE_SOURCE, RateSource.ProjectBased, { shouldValidate: true });
          setValue(FIELD_RATE, typeof projectRate === 'number' ? String(projectRate) : '', {
            shouldValidate: true,
          });
        }
        setValue(FIELD_CURRENCY, apiCurrency, { shouldValidate: true });
      }
    }
  }

  function resetSavedRateValues() {
    setSavedRateValues(null);
    hasRatesDataLoadedOnceRef.current = false;
    // The modal component does not unmount between opens (only the <Form key=...> subtree does),
    // so without this a second open would inherit the first session's rate source as "previous".
    prevRateSourceRef.current = undefined;
    maskedRateBeforeCustomRef.current = null;
  }

  return { handleBillableChange, resetSavedRateValues };
}
